import { use, useMemo, type PropsWithChildren } from "react";
import { useQuery } from "@tanstack/react-query";
import { useRoute } from "@softmax/web-features/navigation";
import { isCommunityRoom, isCreatorRoom } from "@softmax/web-features/coworlds";
import {
  ProductViewerContext,
  SiteFooter,
  SiteNav,
} from "@softmax/web-features/site";
import {
  CoworldSidebar,
  SignedInRail,
  PillCoworldMenu,
} from "@/features/site-shell";
import { gameWikiQueryOptions } from "../game.queries";
import type { GamePageContext } from "../game-types";
import { gameRoomNavigation } from "./game-room-navigation";
import styles from "./game-frame.module.css";

export function GameRooms({
  game,
  children,
}: PropsWithChildren<{ readonly game: GamePageContext }>) {
  const route = useRoute();
  const { signedIn } = use(ProductViewerContext);
  const theater =
    (route.name === "game.home" && game.presentation === "theater") ||
    route.name === "game.league";
  const navigation = useQuery({
    ...gameWikiQueryOptions(game.coworldName),
    enabled: !theater,
  });
  const nav = useMemo(
    () =>
      gameRoomNavigation({
        game,
        wikiPages: navigation.data?.wikiPages ?? null,
      }),
    [game, navigation.data],
  );

  const footer =
    route.name !== "game.start" ? (
      <div data-site-footer>
        <SiteFooter />
      </div>
    ) : null;

  if (theater || !nav)
    return (
      <>
        {children}
        {footer}
      </>
    );

  let content = children;

  if (isCommunityRoom({ route, corpusSlug: game.corpusSlug })) {
    content = <div className={styles.pillBand}>{children}</div>;
  } else if (!isCreatorRoom({ route, corpusSlug: game.corpusSlug })) {
    content = (
      <div data-shell-page>
        <SiteNav
          shellPill
          coworld={nav}
          signedIn={signedIn}
          hasRail={signedIn}
        />
        <SignedInRail
          session={signedIn}
          sidebar={<CoworldSidebar coworld={nav} />}
        >
          {children}
        </SignedInRail>
      </div>
    );
  }

  return (
    <>
      {game.address.catalog_status === "unlisted" ? (
        <div className="px-4 py-2 text-right text-xs text-foreground-muted">
          Unlisted game
        </div>
      ) : null}
      <PillCoworldMenu nav={nav} />
      {content}
      {footer}
    </>
  );
}
